import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { DatePipe } from '@angular/common'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import moment from 'moment'
import { combineLatest, filter, interval, Subject, Subscription, take, takeUntil } from 'rxjs'
import { KeySystemNavConfig } from '../../../configuration/key.system.nav.config'
import { KeySystem, Structure, StructureDetails } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingStructureService, ReportName } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { Accept, ReportsService } from '../../../services/reports.service'
import { ChamberTypes } from '../../../constants/chamber-types'
import { environment } from '../../../../environments/environment'
import { KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { ToastService } from '@overtur-monorepo/keying-shared'
import { ReportsDialogComponent } from '../../../dialogs/reports-dialog/reports-dialog.component'
import { DialogService } from '../../../services/dialog.service'

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    standalone: false
})
export class ReportsComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort

  destroy$ = new Subject()

  structure: Structure
  keySystem: KeySystem
  title: string
  chamberTypes = ChamberTypes
  structureDetails: StructureDetails

  columns = ['runNewReport', 'name', 'description', 'reportTypeDescription', 'date', 'status']
  reports = new MatTableDataSource()

  apiSubscription: Subscription | null = null
  interval = 10

  constructor(
    private reportsService: ReportsService,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private keySystemNavConfig: KeySystemNavConfig,
    private keyingDataSetsService: KeyingDataSetsService,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private toastService: ToastService,
    private datePipe: DatePipe
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    combineLatest([
      this.keyingStructureService.structure$.pipe(filter((x) => !!x)),
      this.keyingSystemService.keySystem$.pipe(filter((x) => !!x)),
      this.keyingDataSetsService.structureDetails$.pipe(filter((x) => !!x)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([structure, keySystem, structureDetails]: [Structure, KeySystem, StructureDetails]) => {
        this.structure = structure
        this.keySystem = keySystem
        this.structureDetails = structureDetails
        this.keySystemNavConfig.setNav('Reports', this.structure, this.keySystem)

        this.title =
          structure?.name + ', ' + structure?.city + (structure?.stateCode ? ', ' + structure?.stateCode : '')
        this.reports.data = [
          {
            name: 'Overview',
            description: 'Report with notes and cylinder list - INTERNAL USE ONLY',
            accept: Accept.OverviewReport,
            status: null,
            id: 1,
          },
        ]

        if (this.keySystem.statusId === 13) {
          this.reports.data.push(
            {
              name: 'Bitting List',
              description: 'Report for list of bittings',
              status: null,
              id: 2,
            },
            {
              name: 'Pinning Chart',
              description: 'Report for pinning charts',
              accept: Accept.PinningReport,
              status: null,
              id: 5,
            },
            {
              name: 'Pinning Chart-SL',
              description: 'Report for pinning chart-sl',
              accept: Accept.ShearlineReport,
              status: null,
              id: 6,
            },
            {
              name: 'Export For Canada',
              description: 'Import codes for AS400',
              accept: Accept.CanadaReport,
              status: null,
              id: 3,
            },
            {
              name: 'Key System Design',
              description: 'Customer facing design report',
              accept: Accept.DesignReport,
              status: null,
              id: 4,
            },
            {
              name: 'Site Master Export',
              description: 'Site Master importable bitting list',
              accept: Accept.SiteMasterReport,
              status: null,
              id: 7,
            }
          )
        }

        if (this.structureDetails.isFromIcom === true) {
          this.reports.data.push(
            {
              name: 'ICOM Master Key Report',
              description: 'Historical ICOM Master Key Report - INTERNAL USE ONLY',
              accept: Accept.ICOMMasterKeyReport,
              status: null,
              id: 0,
            },
            {
              name: 'ICOM Change Key Report',
              description: 'Historical ICOM Change Key Report - INTERNAL USE ONLY',
              accept: Accept.ICOMChangeKeyReport,
              status: null,
              id: -1,
            }
          )
        }
        this.getReportStatus(this.structure)
        this.reports.sort = this.sort
      })
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe()
    }
  }

  /* istanbul ignore next */
  chamberType(chamberTypeId) {
    return this.chamberTypes[chamberTypeId]
  }

  /* istanbul ignore next */
  getReportStatus(element) {
    this.reportsService.getReportStatus(element.id, this.keySystem.id).subscribe((reportResponse: any) => {
      this.reports.data.forEach((report: any) => {
        let existReport = reportResponse.filter((x: any) => x.reportTypeId === report.id)
        if (existReport.length === 0 && (report.id === 2 || report.id === 5 || report.id === 6)) {
          const reportId = report.id === 2 ? 9 : report.id === 5 ? 10 : 11
          existReport = reportResponse.filter((x: any) => x.reportTypeId === reportId)
        }

        if (existReport.length > 0) {
          report.status = existReport[0].statusId
          report.reportTypeDescription = this.getReportTypeDescription(existReport[0])
          report.createdOn = this.datePipe.transform(existReport[0].createdOn, 'MMM d, y')
        }
      })
      this.shouldScheduleRefresh(reportResponse)
    })
  }

  /* istanbul ignore next */
  generateReport(element, isRequeue): void {
    let childHttpRoute = ''
    let acceptHeader
    let reportName

    if (element.id === 0) {
      reportName = ReportName.ICOMMasterKeyReport

      this.reportsService
        .getICOMReport(
          this.structure.structureNumber,
          this.structure.id,
          this.keySystem.id,
          1,
          Accept.ICOMMasterKeyReport
        )
        .subscribe({
          next: (file) => {
            // It is necessary to create a new blob object with mime-type explicitly set
            // otherwise only Chrome works like it should
            const newBlob = new Blob([file], { type: file.type })

            // IE doesn't allow using a blob object directly as link href
            // instead it is necessary to use msSaveOrOpenBlob
            if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
              ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
              return
            }

            reportName = `${this.structure.structureNumber}.${this.keySystem.topMasterKeyName}.${
              element.name
            }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`

            // For other browsers:
            // Create a link pointing to the ObjectURL containing the blob.
            const data = window.URL.createObjectURL(newBlob)

            const link = document.createElement('a')
            link.href = data

            link.download = reportName
            // this is necessary as link.click() does not work on the latest firefox
            link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

            setTimeout(() => {
              // For Firefox it is necessary to delay revoking the ObjectURL
              window.URL.revokeObjectURL(data)
              link.remove()
            }, 100)
          },
          error: (error) => {
            this.errorService.throw(error)
          },
        })
    } else if (element.id === -1) {
      reportName = ReportName.ICOMChangeKeyReport

      this.reportsService
        .getICOMReport(
          this.structure.structureNumber,
          this.structure.id,
          this.keySystem.id,
          2,
          Accept.ICOMChangeKeyReport
        )
        .subscribe({
          next: (file) => {
            // It is necessary to create a new blob object with mime-type explicitly set
            // otherwise only Chrome works like it should
            const newBlob = new Blob([file], { type: file.type })

            // IE doesn't allow using a blob object directly as link href
            // instead it is necessary to use msSaveOrOpenBlob
            if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
              ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
              return
            }

            reportName = `${this.structure.structureNumber}.${this.keySystem.topMasterKeyName}.${
              element.name
            }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`

            // For other browsers:
            // Create a link pointing to the ObjectURL containing the blob.
            const data = window.URL.createObjectURL(newBlob)

            const link = document.createElement('a')
            link.href = data

            link.download = reportName
            // this is necessary as link.click() does not work on the latest firefox
            link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

            setTimeout(() => {
              // For Firefox it is necessary to delay revoking the ObjectURL
              window.URL.revokeObjectURL(data)
              link.remove()
            }, 100)
          },
          error: (error) => {
            this.errorService.throw(error)
          },
        })
    } else {
      if (element.id == 1) {
        childHttpRoute = 'Overview'
        acceptHeader = Accept.OverviewReport
        reportName = ReportName.OverviewReport
      } else if (element.id == 3) {
        childHttpRoute = 'CanadaExport'
        acceptHeader = Accept.CanadaReport
        reportName = ReportName.CanadaReport
      } else if (element.id == 4) {
        childHttpRoute = 'KeySystemDesign'
        acceptHeader = Accept.DesignReport
        reportName = ReportName.DesignReport
      } else if (element.id == 7) {
        childHttpRoute = 'SiteMaster'
        acceptHeader = Accept.SiteMasterReport
        reportName = ReportName.SiteMasterReport
      }

      if (element.id === 2 || element.id === 5 || element.id === 6) {
        if (isRequeue) {
          this.handleReportequestRequeue(element.id)
          return
        } else {
          this.showReportDialog(ReportsDialogComponent, { id: element.id }, true)
          return
        }
      }

      this.keyingStructureService
        .submitFullReportRequest(this.structure.id, this.keySystem.id, childHttpRoute, acceptHeader, isRequeue)
        .pipe(take(1))
        .subscribe(() => {
          this.errorService.notify(
            'Generating Report',
            'This report will take some time to build. Please come back later to download.'
          )
          setTimeout(() => {
            this.toastService.close()
          }, 8000)
          this.getReportStatus(this.structure)
        })
    }
  }

  /* istanbul ignore next */
  showReportDialog(dialog, data?, autoFocus = false): void {
    const dialogRef = this.dialogService.open(dialog, data, autoFocus)
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log('Dialog closed with result:', result)
        if (result.reportType) {
          this.handleReportequest(result, false)
        }
      } else {
        console.log('Condition not met or result is null')
      }
    })
  }

  /* istanbul ignore next */
  handleReportequest(payload: any, isRequeue: boolean) {
    if (payload.reportTypeId === 2) {
      this.handleBittingListReportRequest(payload, isRequeue)
    } else if (payload.reportTypeId === 5) {
      this.handlePinningReportRequest(payload, isRequeue)
    } else if (payload.reportTypeId === 6) {
      this.handleShearlineReportRequest(payload, isRequeue)
    }
  }

  /* istanbul ignore next */
  handleReportequestRequeue(reportTypeId: number) {
    const payload = { reportType: 'Full' }

    if (reportTypeId === 2) {
      this.handleBittingListReportRequest(payload, true)
    } else if (reportTypeId === 5) {
      this.handlePinningReportRequest(payload, true)
    } else if (reportTypeId === 6) {
      this.handleShearlineReportRequest(payload, true)
    }
  }

  /* istanbul ignore next */
  handleBittingListReportRequest(payload: any, isRequeue: boolean) {
    if (payload.reportType === 'Full') {
      this.submitFullReportRequest('BittingList', Accept.BittingListFullReport, isRequeue)
    } else if (payload.reportType === 'Partial') {
      this.submitPartialReportRequest('PartialBittingList', Accept.BittingListPartialReport, payload)
    }
  }

  /* istanbul ignore next */
  handlePinningReportRequest(payload: any, isRequeue: boolean) {
    if (payload.reportType === 'Full') {
      this.submitFullReportRequest('PinningChart', Accept.PinningReport, isRequeue)
    } else if (payload.reportType === 'Partial') {
      this.submitPartialReportRequest('PartialPinningChart', Accept.PartialPinningReport, payload)
    }
  }

  /* istanbul ignore next */
  handleShearlineReportRequest(payload: any, isRequeue: boolean) {
    if (payload.reportType === 'Full') {
      this.submitFullReportRequest('PinningChartSL', Accept.ShearlineReport, isRequeue)
    } else if (payload.reportType === 'Partial') {
      this.submitPartialReportRequest('PartialPinningChartSL', Accept.PartialShearlineReport, payload)
    }
  }

  /* istanbul ignore next */
  submitFullReportRequest(childHttpRoute: string, acceptHeader: string, isRequeue = false) {
    this.keyingStructureService
      .submitFullReportRequest(this.structure.id, this.keySystem.id, childHttpRoute, acceptHeader, isRequeue)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.errorService.notify(
            'Generating Report',
            'This report will take some time to build. Please come back later to download.'
          )
          setTimeout(() => {
            this.toastService.close()
          }, 8000)
          this.getReportStatus(this.structure)
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  submitPartialReportRequest(childHttpRoute: string, acceptHeader: string, payload: any) {
    this.keyingStructureService
      .submitPartialReportRequest(this.structure.id, this.keySystem.id, childHttpRoute, acceptHeader, payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.errorService.notify(
            'Generating Report',
            'This report will take some time to build. Please come back later to download.'
          )
          setTimeout(() => {
            this.toastService.close()
          }, 8000)
          this.getReportStatus(this.structure)
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  downloadReport(element, reportTypeId?): void {
    let reportName
    const pinCount = this.chamberType(this.structure.chamberTypeId)
    if (reportTypeId === null || reportTypeId === undefined) {
      this.reportsService
        .downloadReport(this.structure.id, this.keySystem.id, element.id)
        .pipe(take(1))
        .subscribe({
          next: (file) => {
            // It is necessary to create a new blob object with mime-type explicitly set
            // otherwise only Chrome works like it should
            const newBlob = new Blob([file], { type: file.type })

            // IE doesn't allow using a blob object directly as link href
            // instead it is necessary to use msSaveOrOpenBlob
            if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
              ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
              return
            }

            if (element.id === 7) {
              reportName = `SM.${this.structure.structureNumber}.${this.keySystem.topMasterKeyName}.${
                this.structure.keywayFamilyName
              }.${this.structure.combinatingRuleSetName}.PinCount(#${pinCount}).${moment().format('MMDDYY')}.${
                file.type.split('/')[1]
              }`
            } else {
              reportName = `${this.structure.structureNumber}.${this.keySystem.topMasterKeyName}.${
                element.name
              }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`
            }

            // For other browsers:
            // Create a link pointing to the ObjectURL containing the blob.
            const data = window.URL.createObjectURL(newBlob)

            const link = document.createElement('a')
            link.href = data
            // link.download = `${this.structure.structureNumber}.${this.keySystem.topMasterKeyName}.${
            //   element.name
            // }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`
            link.download = reportName
            // this is necessary as link.click() does not work on the latest firefox
            link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

            setTimeout(() => {
              // For Firefox it is necessary to delay revoking the ObjectURL
              window.URL.revokeObjectURL(data)
              link.remove()
            }, 100)
          },
          error: (error) => {
            this.errorService.throw(error)
          },
        })
    }
  }

  /* istanbul ignore next */
  scheduleRefreshReports() {
    const schedule = interval(this.interval * 1000)
    this.apiSubscription = schedule.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getReportStatus(this.structure)
    })
  }

  /* istanbul ignore next */
  shouldScheduleRefresh(reports) {
    if (environment.flags.autoReload) {
      let shouldSchedule = false
      reports.map((report) => {
        if (report.statusId === 1 || report.statusId === 2) {
          shouldSchedule = true
        } else {
          shouldSchedule = false
        }
      })
      if (!this.apiSubscription && shouldSchedule) {
        this.scheduleRefreshReports()
      } else if (this.apiSubscription && !shouldSchedule) {
        this.apiSubscription.unsubscribe()
        this.apiSubscription = null
      }
    }
  }

  /* istanbul ignore next */
  disableRunNewReport(element): boolean {
    if (
      element.status === null ||
      element.status === undefined ||
      (element.status === 3 && (element.id === 2 || element.id === 5 || element.id === 6))
    ) {
      return false
    }
    return true
  }

  /* istanbul ignore next */
  getReportTypeDescription(report): string {
    if (report.statusId === null || report.statusId === undefined) {
      return '[N/A]'
    }
    if (report.reportTypeId === 9 || report.reportTypeId === 10 || report.reportTypeId === 11) {
      return 'Partial Report'
    } else {
      return 'Full Report'
    }
  }
}
